import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);
const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userType, setUserType] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const savedUser = localStorage.getItem('carmagard_user');
    const savedType = localStorage.getItem('carmagard_user_type');
    if (savedUser && savedType) {
      setUser(JSON.parse(savedUser));
      setUserType(savedType);
    }
    setLoading(false);
  }, []);

  const login = async (email, password, type) => {
    try {
      let endpoint = type === 'customer' ? '/users/login' : 
                     type === 'mechanic' ? '/mechanics/login' :
                     '/admin/login';
      
      let response;
      // All endpoints now accept JSON body
      response = await axios.post(`${API}${endpoint}`, { email, password });
      
      const userData = response.data.user || response.data.mechanic || response.data.admin;
      setUser(userData);
      setUserType(type);
      localStorage.setItem('carmagard_user', JSON.stringify(userData));
      localStorage.setItem('carmagard_user_type', type);
      localStorage.setItem('carmagard_token', response.data.token || userData._id || 'admin');
      return { success: true, user: userData };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Login failed' };
    }
  };

  const register = async (data, type) => {
    try {
      const endpoint = type === 'customer' ? '/users/register' : '/mechanics/register';
      const response = await axios.post(`${API}${endpoint}`, data);
      return { success: true, user: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Registration failed' };
    }
  };

  const logout = () => {
    setUser(null);
    setUserType(null);
    localStorage.removeItem('carmagard_user');
    localStorage.removeItem('carmagard_user_type');
    localStorage.removeItem('carmagard_token');
  };

  return (
    <AuthContext.Provider value={{ user, userType, loading, login, register, logout, isAuthenticated: !!user }}>
      {children}
    </AuthContext.Provider>
  );
};

// Booking Context
const BookingContext = createContext(null);
const useBooking = () => useContext(BookingContext);

const BookingProvider = ({ children }) => {
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectedServices, setSelectedServices] = useState([]);
  const [location, setLocation] = useState(null);
  const [appointment, setAppointment] = useState(null);

  const clearBooking = () => {
    setSelectedVehicle(null);
    setSelectedServices([]);
    setLocation(null);
    setAppointment(null);
  };

  const totalCost = selectedServices.reduce((sum, s) => sum + (s.price || 0), 0);

  return (
    <BookingContext.Provider value={{
      selectedVehicle, setSelectedVehicle,
      selectedServices, setSelectedServices,
      location, setLocation,
      appointment, setAppointment,
      totalCost, clearBooking
    }}>
      {children}
    </BookingContext.Provider>
  );
};

// ==================== LANDING PAGE COMPONENTS ====================

// FAQ Accordion Item
const FAQItem = ({ question, answer, isOpen, onClick }) => (
  <div className="faq-item">
    <button className="faq-question" onClick={onClick}>
      <span>{question}</span>
      <span className="faq-icon">{isOpen ? '−' : '+'}</span>
    </button>
    {isOpen && <div className="faq-answer">{answer}</div>}
  </div>
);

// Service Card
const ServiceCard = ({ icon, title, description, image }) => (
  <div className="service-card">
    {image && (
      <div className="service-image">
        <img src={image} alt={title} loading="lazy" />
      </div>
    )}
    <div className="service-content">
      <span className="service-icon">{icon}</span>
      <h4>{title}</h4>
      <p>{description}</p>
    </div>
  </div>
);

// Trust Card
const TrustCard = ({ icon, title, description }) => (
  <div className="trust-card">
    <span className="trust-icon">{icon}</span>
    <h4>{title}</h4>
    <p>{description}</p>
  </div>
);

// Review Card
const ReviewCard = ({ text, author, role }) => (
  <div className="review-card">
    <p className="review-text">"{text}"</p>
    <div className="review-author">
      <span className="review-name">{author}</span>
      <span className="review-role">{role}</span>
    </div>
  </div>
);

// Pricing Card
const PricingCard = ({ title, description, includes, cta }) => (
  <div className="pricing-card">
    <h4>{title}</h4>
    <p className="pricing-desc">{description}</p>
    <ul className="pricing-includes">
      {includes.map((item, i) => <li key={i}>✓ {item}</li>)}
    </ul>
    <p className="pricing-note">{cta}</p>
  </div>
);

// ==================== LANDING PAGE ====================
const LandingPage = () => {
  const navigate = useNavigate();
  const [selectedRole, setSelectedRole] = useState('customer');
  const [openFAQ, setOpenFAQ] = useState(null);
  const [howItWorksRole, setHowItWorksRole] = useState('customer');
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [showInstallModal, setShowInstallModal] = useState(false);
  const [showInstallBanner, setShowInstallBanner] = useState(false);

  // PWA Install functionality
  useEffect(() => {
    const DISMISS_KEY = 'cg_landing_install_dismissed_until';
    const isInAppBrowser = () => {
      const ua = (navigator.userAgent || '').toLowerCase();
      return ['fban', 'fbav', 'instagram', 'line/', 'twitter', 'micromessenger',
              'tiktok', 'pinterest/', 'wv)', '; wv'].some((m) => ua.indexOf(m) >= 0);
    };
    const isStandalone = () => {
      try {
        if (window.matchMedia('(display-mode: standalone)').matches) return true;
        if (window.matchMedia('(display-mode: minimal-ui)').matches) return true;
        if (typeof navigator.standalone === 'boolean' && navigator.standalone) return true;
      } catch (_) {}
      return false;
    };
    const dismissedActive = () => {
      try {
        const until = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
        return Number.isFinite(until) && Date.now() < until;
      } catch (_) { return false; }
    };
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      setIsInstallable(true);
    };
    const handleAppInstalled = () => {
      setShowInstallBanner(false);
      setIsInstallable(false);
      try {
        const ms = 365 * 24 * 60 * 60 * 1000;
        localStorage.setItem(DISMISS_KEY, String(Date.now() + ms));
      } catch (_) {}
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // Auto-pop banner on landing for non-installed, non-dismissed users.
    // Wait long enough for `beforeinstallprompt` to potentially fire on
    // Chromium; if it doesn't, we'll still show the banner and route the
    // CTA to the manual instructions modal.
    let timer = null;
    if (!isStandalone() && !isInAppBrowser() && !dismissedActive()) {
      timer = setTimeout(() => setShowInstallBanner(true), 3500);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      if (timer) clearTimeout(timer);
    };
  }, []);

  const dismissInstallBanner = (days = 7) => {
    try {
      const ms = days * 24 * 60 * 60 * 1000;
      localStorage.setItem('cg_landing_install_dismissed_until', String(Date.now() + ms));
    } catch (_) {}
    setShowInstallBanner(false);
  };

  const handleInstallClick = async () => {
    if (installPrompt) {
      installPrompt.prompt();
      const { outcome } = await installPrompt.userChoice;
      if (outcome === 'accepted') {
        setIsInstallable(false);
        dismissInstallBanner(365);
      } else {
        dismissInstallBanner(7);
      }
      setInstallPrompt(null);
    } else {
      // Show nice modal with platform-specific instructions
      setShowInstallModal(true);
    }
  };

  // Detect device type for install instructions
  const getDeviceType = () => {
    const ua = navigator.userAgent || '';
    const platform = navigator.platform || '';
    // Check for iOS
    if (/iPad|iPhone|iPod/.test(ua) || (platform === 'MacIntel' && navigator.maxTouchPoints > 1)) {
      return 'ios';
    }
    // Check for Android
    if (/Android/.test(ua)) {
      return 'android';
    }
    return 'desktop';
  };
  
  const deviceType = getDeviceType();

  // Scroll to section
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Get CTA route based on selected role - direct to PWA HTML files
  const getStartedRoute = selectedRole === 'customer' ? '/pwa/customer/index.html' : '/pwa/mechanic/index.html';
  const loginRoute = selectedRole === 'customer' ? '/pwa/customer/index.html' : '/pwa/mechanic/index.html';

  // Handle role selection - redirect to PWA HTML files directly
  const handleRoleSelect = (role) => {
    setSelectedRole(role);
    window.location.href = role === 'customer' ? '/pwa/customer/index.html' : '/pwa/mechanic/index.html';
  };

  // Customer steps
  const customerSteps = [
    { num: '1', title: 'Choose Service', desc: 'Choose vehicle-aware maintenance or repair and review the price before checkout.' },
    { num: '2', title: 'Pick Time & Location', desc: 'Choose instant availability or schedule service at home, work, or another service location.' },
    { num: '3', title: 'Get Dispatched', desc: 'Your service request is dispatched to a member of the CarmaGard team.' },
    { num: '4', title: 'Track & Pay', desc: 'Track progress, receive updates, and keep payment and service records together.' }
  ];

  // Mechanic steps
  const mechanicSteps = [
    { num: '1', title: 'Create Profile', desc: 'Set up your mechanic profile with skills and certifications.' },
    { num: '2', title: 'Set Availability', desc: 'Define your service area and working hours.' },
    { num: '3', title: 'Accept Jobs', desc: 'Browse and accept jobs that fit your schedule.' },
    { num: '4', title: 'Get Paid Fast', desc: 'Receive weekly payouts directly to your account.' }
  ];

  // Services data
  const services = [
    { icon: '🛢️', title: 'Oil Change', description: 'Vehicle-compatible oil and filter service for your exact car', image: 'https://images.unsplash.com/photo-1487754180451-c456f719a1fc?w=300&h=200&fit=crop' },
    { icon: '🛞', title: 'Brakes', description: 'Pads, rotors, and complete brake system service', image: 'https://images.unsplash.com/photo-1568644305664-5c7c2f88fa65?w=300&h=200&fit=crop' },
    { icon: '🔋', title: 'Battery', description: 'Testing, replacement, and jump-start services', image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=300&h=200&fit=crop' },
    { icon: '⚙️', title: 'Tune-Up', description: 'Spark plugs, filters, and performance optimization', image: 'https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=300&h=200&fit=crop' },
    { icon: '💧', title: 'Fluids & Wipers', description: 'All fluid top-offs and wiper blade replacement', image: 'https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=300&h=200&fit=crop' },
      { icon: '🎯', title: 'Filters', description: 'Air, cabin, oil, and fuel filter replacement', image: 'https://images.unsplash.com/photo-1619642751034-765dfdf7c58e?w=300&h=200&fit=crop' },
    { icon: '📋', title: 'Pre-Purchase', description: 'Complete inspection before you buy a used car', image: 'https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=300&h=200&fit=crop' }
  ];

  // FAQ data
  const faqs = [
    { q: 'How does mobile service work?', a: 'Our mechanics come to your location—home, office, or parking lot—with all the tools and parts needed. No tow truck, no waiting room.' },
    { q: 'Do I need to be present during service?', a: 'It depends on the job. For simple services, you can leave your keys. For diagnostics or repairs, we recommend being available for questions.' },
    { q: 'How do I pay?', a: 'Payment is handled securely through the app after your service is complete. We accept all major credit cards and digital wallets.' },
    { q: 'How are mechanics vetted?', a: 'All mechanics go through background checks, credential verification, and must maintain positive ratings to stay on the platform.' },
    { q: 'What areas do you serve?', a: 'We\'re currently available in select metropolitan areas and expanding. Enter your zip code in the app to check availability.' },
    { q: 'What if my car needs more work than expected?', a: 'Your mechanic will explain any additional work needed and provide a revised estimate. You approve before any extra work begins.' },
    { q: 'Can mechanics choose which jobs to accept?', a: 'Yes! Mechanics are independent contractors who accept jobs based on their availability, location, and expertise.' },
    { q: 'How do cancellations work?', a: 'Free cancellation up to 2 hours before your appointment. Late cancellations may incur a small fee to compensate the mechanic.' }
  ];

  const handleAdminClick = () => {
    navigate('/admin');
  };

  return (
    <div className="landing-page">
      {/* Header */}
      <header className="landing-header">
        <div className="header-left">
          <div className="logo-box">
            <img src="/logo.png" alt="CarmaGard" className="logo-img" onError={(e) => { e.target.style.display = 'none'; }} />
          </div>
        </div>
        
        <nav className="header-nav">
          <button onClick={() => scrollToSection('how-it-works')}>How It Works</button>
          <button onClick={() => scrollToSection('services')}>Services</button>
          <button onClick={() => scrollToSection('pricing')}>Pricing</button>
          <button onClick={() => scrollToSection('trust')}>Trust</button>
          <button onClick={() => scrollToSection('faq')}>FAQ</button>
        </nav>

        <div className="header-buttons">
          <button 
            onClick={handleInstallClick}
            className="install-btn"
            data-testid="install-app-btn"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"/>
            </svg>
            Install App
          </button>
          <button 
            onClick={() => handleRoleSelect('customer')} 
            className={`role-btn ${selectedRole === 'customer' ? 'active' : ''}`}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z"/>
            </svg>
            Customer
          </button>
          <button 
            onClick={() => handleRoleSelect('mechanic')} 
            className={`role-btn ${selectedRole === 'mechanic' ? 'active' : ''}`}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
            </svg>
            Mechanic
          </button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-urgency" data-testid="hero-urgency">
            <span className="hero-urgency-dot"></span>
            Appointments available &mdash; choose instant or scheduled car care
          </div>
          <h1 className="hero-title">
            Professional<br />
            Car Care<br />
            That Comes<br />
            To You
          </h1>
          <p className="hero-subtitle">
            Maintenance and repairs at home, work, or your service location.<br />
            Convenient care, clear pricing, and progress updates in one place.
          </p>

          {/* Benefits */}
          <div className="hero-benefits">
            <div className="benefit-item">
              <span>⚡</span> Same-day scheduling
            </div>
            <div className="benefit-item">
              <span>✓</span> Skilled automotive professionals
            </div>
            <div className="benefit-item">
              <span>📱</span> Upfront estimates & status updates
            </div>
          </div>

          {/* CTAs */}
          <div className="hero-ctas">
            <button onClick={() => window.location.href = getStartedRoute} className="primary-cta">
              Get Started
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </button>
            <button onClick={() => scrollToSection('how-it-works')} className="secondary-cta">
              How It Works
            </button>
          </div>

          {/* Trust Row */}
          <div className="trust-row">
            <span>Transparent pricing</span>
            <span className="dot">•</span>
            <span>Secure payments</span>
            <span className="dot">•</span>
            <span>Real-time updates</span>
          </div>

          {/* Trust Badges (new) */}
          <div className="hero-trust-badges" data-testid="hero-trust-badges">
            <div className="hero-trust-badge">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
              Vehicle-aware service options
            </div>
            <div className="hero-trust-badge">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 6L9 17l-5-5"/></svg>
              Service progress in one app
            </div>
            <div className="hero-trust-badge">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M3 10h18M8 2v4M16 2v4"/></svg>
              Instant or scheduled booking
            </div>
          </div>
        </div>
        <div className="hero-image">
          <div className="hero-image-stack">
            <img 
              src="https://images.unsplash.com/photo-1632733711679-529326f6db12?w=900&h=680&fit=crop&q=85"
              alt="Automotive professional providing car care at the customer's location"
              className="hero-img hero-img-primary"
              loading="eager"
              fetchpriority="high"
              width="900"
              height="680"
            />
            <img 
              src="https://images.pexels.com/photos/5835359/pexels-photo-5835359.jpeg?auto=compress&w=500&h=360&fit=crop"
              alt="Mechanic performing roadside brake service"
              className="hero-img-inset"
              loading="lazy"
              width="500"
              height="360"
            />
          </div>
        </div>
      </section>

      {/* Customer experience capabilities */}
      <section className="social-proof-band" data-testid="customer-experience-capabilities" aria-label="CarmaGard customer experience">
        <div className="social-proof-stat">
          <div className="stat-num">Upfront</div>
          <div className="stat-label">pricing before checkout</div>
        </div>
        <div className="social-proof-stat">
          <div className="stat-num">On-site</div>
          <div className="stat-label">service at your location</div>
        </div>
        <div className="social-proof-stat">
          <div className="stat-num">Live</div>
          <div className="stat-label">job status updates</div>
        </div>
        <div className="social-proof-stat">
          <div className="stat-num">Secure</div>
          <div className="stat-label">checkout and records</div>
        </div>
      </section>

      <section className="landing-performance-band" aria-label="CarmaGard service confidence">
  <div className="landing-performance-copy"><span className="landing-eyebrow">CAR CARE, BUILT AROUND YOUR DAY</span><h2>The shop-quality process. Without the waiting room.</h2><p>Choose your vehicle and service, confirm the location, and follow every step from dispatch through completion.</p></div>
  <div className="landing-performance-points"><article><span>01</span><strong>Vehicle-aware service</strong><p>Compatibility checks help match fluids, parts, and service options to your car.</p></article><article><span>02</span><strong>Clear protected pricing</strong><p>Your checkout price is shown before payment, with job and payment records kept in one place.</p></article><article><span>03</span><strong>Live service visibility</strong><p>See assignment, arrival, progress, notes, and completion updates from your phone.</p></article></div>
</section>

      {/* How It Works */}
      <section id="how-it-works" className="section how-it-works-section">
        <h2 className="section-title">How CarmaGard Works</h2>
        
        <div className="role-toggle">
          <button 
            className={howItWorksRole === 'customer' ? 'active' : ''} 
            onClick={() => setHowItWorksRole('customer')}
          >
            For Customers
          </button>
          <button 
            className={howItWorksRole === 'mechanic' ? 'active' : ''} 
            onClick={() => setHowItWorksRole('mechanic')}
          >
            For Mechanics
          </button>
        </div>

        <div className="steps-grid">
          {(howItWorksRole === 'customer' ? customerSteps : mechanicSteps).map((step, i) => (
            <div key={i} className="step-card">
              <div className="step-number">{step.num}</div>
              <h4>{step.title}</h4>
              <p>{step.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="section services-section">
        <h2 className="section-title">Our Services</h2>
        <p className="section-subtitle">Professional maintenance and repairs at your doorstep</p>
        
        <div className="services-grid">
          {services.map((service, i) => (
            <ServiceCard key={i} {...service} />
          ))}
        </div>

        <button onClick={() => window.location.href = getStartedRoute} className="primary-cta center-cta">
          Book a Service
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M5 12h14M12 5l7 7-7 7"/>
          </svg>
        </button>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="section pricing-section">
        <h2 className="section-title">Simple & Transparent Pricing</h2>
        <p className="section-subtitle">No hidden fees. You'll see your estimate before confirming.</p>
        
        <div className="pricing-grid">
          <PricingCard 
            title="Starter Services"
            description="Common maintenance to keep your car running smooth"
            includes={['Oil changes', 'Filter replacements', 'Fluid top-offs', 'Wiper blades']}
            cta="Price varies by vehicle & oil type"
          />
          <PricingCard 
            title="Repair Services"
            description="Fix issues before they become bigger problems"
            includes={['Brake pads & rotors', 'Battery replacement', 'Spark plugs', 'Belt replacements']}
            cta="Price varies by parts & labor"
          />
          <PricingCard 
            title="Diagnostics"
            description="Find out what's wrong with your vehicle"
            includes={['OBD2 code scan', 'System analysis', 'Detailed report', 'Repair recommendations']}
            cta="Flat rate diagnostic fee"
          />
        </div>

        <button onClick={() => window.location.href = getStartedRoute} className="primary-cta center-cta">
          {selectedRole === 'customer' ? 'Get Estimate' : 'Start Earning'}
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M5 12h14M12 5l7 7-7 7"/>
          </svg>
        </button>
      </section>

      {/* Trust & Safety Section */}
      <section id="trust" className="section trust-section">
        <h2 className="section-title">Trust & Safety</h2>
        <p className="section-subtitle">Your safety and satisfaction are our top priorities</p>
        
        <div className="trust-content">
          <div className="trust-image">
            <img 
              src="https://images.pexels.com/photos/3807517/pexels-photo-3807517.jpeg?auto=compress&cs=tinysrgb&w=600" 
              alt="Professional CarmaGard mechanic"
              loading="lazy"
            />
          </div>
          
          <div className="trust-grid">
            <TrustCard 
              icon="🛡️"
              title="Verified Mechanics"
              description="Automotive professionals managed through CarmaGard service workflows"
            />
            <TrustCard 
              icon="🔒"
              title="Secure Payments"
              description="Your payment info is encrypted and never shared with mechanics directly"
            />
            <TrustCard 
              icon="📍"
              title="Live Status Updates"
              description="Track your mechanic's arrival and service progress in real-time"
            />
            <TrustCard 
              icon="💬"
              title="Support & Resolution"
              description="In-app support tools for service questions and issue resolution"
            />
          </div>
        </div>

      </section>

      {/* App Preview Section      </section>

      {/* App Preview Section — now uses REAL photos instead of mock phones */}
      <section className="section app-preview-section">
        <h2 className="section-title">Built for both sides of the garage</h2>
        <p className="section-subtitle">One platform. Two purpose-built apps. Zero friction.</p>

        <div className="real-app-preview" data-testid="real-app-preview">
          <div className="preview-card" data-testid="preview-card-customer">
            <img
              src="https://images.unsplash.com/photo-1625047509168-a7026f36de04?w=900&h=520&fit=crop&q=85"
              alt="Customer receiving on-site car service at their driveway"
              loading="lazy"
              width="900" height="520"
            />
            <div className="preview-card-body">
              <h3>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z"/></svg>
                For Customers
              </h3>
              <p>Book trusted mechanics, track their arrival in real time, and pay securely — all from your phone.</p>
              <ul className="preview-features">
                <li><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 6L9 17l-5-5"/></svg> 60-second service booking</li>
                <li><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 6L9 17l-5-5"/></svg> Upfront estimate before anyone arrives</li>
                <li><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 6L9 17l-5-5"/></svg> Live progress + mechanic notes &amp; photos</li>
              </ul>
            </div>
          </div>

          <div className="preview-card" data-testid="preview-card-mechanic">
            <img
              src="https://images.pexels.com/photos/4116201/pexels-photo-4116201.jpeg?auto=compress&w=900&h=520&fit=crop"
              alt="Professional mechanic performing on-site diagnostics with a laptop"
              loading="lazy"
              width="900" height="520"
            />
            <div className="preview-card-body">
              <h3>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>
                For Mechanics
              </h3>
              <p>Pick the jobs that fit your day, get paid fast, and build your reputation on a platform that respects your craft.</p>
              <ul className="preview-features">
                <li><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 6L9 17l-5-5"/></svg> Transparent flat-rate payouts from every service</li>
                <li><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 6L9 17l-5-5"/></svg> Choose your service area &amp; working hours</li>
                <li><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 6L9 17l-5-5"/></svg> Tips, ratings &amp; weekly direct deposit</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Why CarmaGard — value-prop grid (new) */}
      <section className="section" aria-labelledby="why-heading" style={{background:'var(--surface-dark-alt, #0f0f0f)'}}>
        <h2 className="section-title" id="why-heading">Why drivers &amp; mechanics choose CarmaGard</h2>
        <p className="section-subtitle">Purpose-built to remove the worst parts of getting &mdash; and doing &mdash; car service.</p>
        <div className="value-grid" data-testid="value-grid">
          <div className="value-card"><div className="value-icon">⏱️</div><h3>Save half a day</h3><p>No tow trucks. No waiting rooms. Work, relax, or keep your day moving while we handle your car right where it is.</p></div>
          <div className="value-card"><div className="value-icon">💳</div><h3>Upfront, honest pricing</h3><p>You see the full estimate before you confirm. No surprise invoices, no line items you can't read.</p></div>
          <div className="value-card"><div className="value-icon">🔧</div><h3>Only verified pros</h3><p>Every mechanic is background-checked, credential-verified, and rating-gated. Unsafe service ends the relationship.</p></div>
          <div className="value-card"><div className="value-icon">📷</div><h3>Proof, not promises</h3><p>Your mechanic delivers notes, parts photos, and a timestamped service log to your phone before they leave.</p></div>
          <div className="value-card"><div className="value-icon">💵</div><h3>Fair pay for mechanics</h3><p>Strict flat-rate payouts from our service catalog &mdash; no hourly games, no hidden skims, tips go to the mechanic.</p></div>
          <div className="value-card"><div className="value-icon">🛟</div><h3>Support when you need it</h3><p>Use CarmaGard's in-app support tools for questions, reschedules, and service issues.</p></div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="section faq-section">
        <h2 className="section-title">Frequently Asked Questions</h2>
        
        <div className="faq-list">
          {faqs.map((faq, i) => (
            <FAQItem 
              key={i}
              question={faq.q}
              answer={faq.a}
              isOpen={openFAQ === i}
              onClick={() => setOpenFAQ(openFAQ === i ? null : i)}
            />
          ))}
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="section final-cta-section">
        <h2>Car care that fits your schedule.</h2>
        <p>Choose the service, time, and location that work for your vehicle and your day.</p>
        
        <div className="final-ctas">
          <button onClick={() => window.location.href = getStartedRoute} className="primary-cta">
            Get Started
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </button>
          <button onClick={() => window.location.href = loginRoute} className="secondary-cta">
            I Have an Account
          </button>
        </div>
        <p className="role-note">Customer or Mechanic — choose your path above.</p>
      </section>

      {/* Partner / credibility strip */}
      <section className="partner-strip" data-testid="partner-strip" aria-label="Trust and compliance">
        <div className="partner-label">Backed by best-in-class partners</div>
        <div className="partner-row">
          <span><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 10h20"/></svg> Stripe secured payments</span>
          <span><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15 15 0 0 1 0 20M12 2a15 15 0 0 0 0 20"/></svg> Google Maps routing</span>
          <span><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg> Background-check verified</span>
          <span><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><path d="M22 4L12 14.01l-3-3"/></svg> Vehicle-aware service catalog</span>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="footer-content">
          <div className="footer-links">
            <a href="#">Terms of Service</a>
            <a href="#">Privacy Policy</a>
            <a href="#">Support</a>
            <a href="#">Mechanic Agreement</a>
          </div>
          <p className="footer-contact">support@carmagard.com</p>
          <p className="footer-copyright" onClick={handleAdminClick}>
            © 2025 CarmaGard. All rights reserved.
          </p>
        </div>
      </footer>

      {/* Auto-popup Install Banner (Iter 165) */}
      {showInstallBanner && (
        <div className="install-banner" data-testid="install-banner-auto" role="dialog" aria-label="Install CarmaGard">
          <div className="install-banner-icon" aria-hidden="true">CG</div>
          <div className="install-banner-body">
            <div className="install-banner-title">Install CarmaGard</div>
            <div className="install-banner-sub">
              {installPrompt ? 'Faster, full-screen, with notifications' : 'Add to your home screen — quick & app-like'}
            </div>
          </div>
          <div className="install-banner-actions">
            <button
              className="install-banner-cta"
              data-testid="install-banner-cta"
              type="button"
              onClick={async () => {
                setShowInstallBanner(false);
                await handleInstallClick();
              }}
            >
              {installPrompt ? 'Install' : 'How'}
            </button>
            <button
              className="install-banner-x"
              data-testid="install-banner-dismiss"
              type="button"
              aria-label="Dismiss"
              onClick={() => dismissInstallBanner(7)}
            >×</button>
          </div>
        </div>
      )}

      {/* Install Instructions Modal */}
      {showInstallModal && (
        <div className="install-modal-overlay" onClick={() => setShowInstallModal(false)}>
          <div className="install-modal" onClick={(e) => e.stopPropagation()}>
            <button className="install-modal-close" onClick={() => setShowInstallModal(false)}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>
            
            <div className="install-modal-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#FF8C42" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"/>
              </svg>
            </div>
            
            <h3 className="install-modal-title">Install CarmaGard</h3>
            <p className="install-modal-subtitle">Add to your home screen for quick access</p>
            
            {deviceType === 'ios' ? (
              <div className="install-steps">
                <div className="install-step">
                  <div className="step-number">1</div>
                  <div className="step-content">
                    <p>Tap the <strong>Share</strong> button</p>
                    <div className="step-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#FF8C42" strokeWidth="2">
                        <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8M16 6l-4-4-4 4M12 2v13"/>
                      </svg>
                    </div>
                  </div>
                </div>
                <div className="install-step">
                  <div className="step-number">2</div>
                  <div className="step-content">
                    <p>Scroll down and tap <strong>"Add to Home Screen"</strong></p>
                    <div className="step-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#FF8C42" strokeWidth="2">
                        <rect x="3" y="3" width="18" height="18" rx="2"/>
                        <path d="M12 8v8M8 12h8"/>
                      </svg>
                    </div>
                  </div>
                </div>
                <div className="install-step">
                  <div className="step-number">3</div>
                  <div className="step-content">
                    <p>Tap <strong>"Add"</strong> to confirm</p>
                    <div className="step-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth="2">
                        <path d="M20 6L9 17l-5-5"/>
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            ) : deviceType === 'android' ? (
              <div className="install-steps">
                <div className="install-step">
                  <div className="step-number">1</div>
                  <div className="step-content">
                    <p>Tap the <strong>menu</strong> button (⋮)</p>
                    <div className="step-icon" style={{ fontSize: '24px' }}>⋮</div>
                  </div>
                </div>
                <div className="install-step">
                  <div className="step-number">2</div>
                  <div className="step-content">
                    <p>Tap <strong>"Install App"</strong> or <strong>"Add to Home Screen"</strong></p>
                    <div className="step-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#FF8C42" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"/>
                      </svg>
                    </div>
                  </div>
                </div>
                <div className="install-step">
                  <div className="step-number">3</div>
                  <div className="step-content">
                    <p>Tap <strong>"Install"</strong> to confirm</p>
                    <div className="step-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth="2">
                        <path d="M20 6L9 17l-5-5"/>
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="install-steps">
                <div className="install-step">
                  <div className="step-number">1</div>
                  <div className="step-content">
                    <p>Click the <strong>install icon</strong> in the address bar</p>
                    <div className="step-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#FF8C42" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"/>
                      </svg>
                    </div>
                  </div>
                </div>
                <div className="install-step">
                  <div className="step-number">2</div>
                  <div className="step-content">
                    <p>Or open browser menu and select <strong>"Install CarmaGard"</strong></p>
                    <div className="step-icon" style={{ fontSize: '24px' }}>⋮</div>
                  </div>
                </div>
              </div>
            )}
            
            <button className="install-modal-btn" onClick={() => setShowInstallModal(false)}>
              Got it!
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== CUSTOMER PORTAL ====================
const CustomerLoginScreen = ({ onNavigate }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }
    setLoading(true);
    setError('');
    const result = await login(email, password, 'customer');
    if (result.success) {
      navigate('/customer/home');
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="login-container customer-theme">
      <div className="logo-section">
        <img src="/logo.png" alt="CarmaGard Logo" onError={(e) => e.target.src = ''} />
      </div>

      <form className="form-section" onSubmit={handleLogin}>
        {error && <div className="error-alert">{error}</div>}

        <label className="form-label">Email</label>
        <input
          type="email"
          className="form-input"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your.email@example.com"
        />

        <label className="form-label">Password</label>
        <div className="password-container">
          <input
            type={showPassword ? 'text' : 'password'}
            className="form-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter your password"
          />
          <button type="button" className="password-toggle" onClick={() => setShowPassword(!showPassword)}>
            {showPassword ? '🙈' : '👁️'}
          </button>
        </div>

        <div className="forgot-password">
          <a href="#">Forgot Password?</a>
        </div>

        <button type="submit" className="login-btn" disabled={loading}>
          {loading ? <span className="spinner"></span> : 'Login'}
        </button>

        <div className="signup-section">
          <span>New user? </span>
          <a href="#" onClick={(e) => { e.preventDefault(); onNavigate('register'); }}>Sign Up</a>
        </div>
      </form>

      <button onClick={() => navigate('/')} className="back-home-btn">← Back to Home</button>
    </div>
  );
};

const CustomerRegisterScreen = ({ onNavigate }) => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register, login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const result = await register(formData, 'customer');
    if (result.success) {
      await login(formData.email, formData.password, 'customer');
      navigate('/customer/home');
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="login-container customer-theme">
      <h1 className="register-title">Create Account</h1>
      {error && <div className="error-alert">{error}</div>}

      <form onSubmit={handleSubmit}>
        {['name', 'email', 'phone', 'password'].map((field) => (
          <div key={field} className="form-group">
            <label className="form-label">{field.charAt(0).toUpperCase() + field.slice(1)}</label>
            <input
              type={field === 'password' ? 'password' : field === 'email' ? 'email' : 'text'}
              className="form-input"
              value={formData[field]}
              onChange={(e) => setFormData({...formData, [field]: e.target.value})}
              required
            />
          </div>
        ))}
        <button type="submit" className="login-btn" disabled={loading}>
          {loading ? <span className="spinner"></span> : 'Create Account'}
        </button>
      </form>

      <div className="signup-section">
        <span>Already have an account? </span>
        <a href="#" onClick={(e) => { e.preventDefault(); onNavigate('login'); }}>Sign In</a>
      </div>
    </div>
  );
};

const CustomerHome = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicles, setSelectedVehicles] = useState([]);
  const [showProfileMenu, setShowProfileMenu] = useState(false);

  useEffect(() => {
    if (user?._id || user?.id) {
      axios.get(`${API}/vehicles/user/${user._id || user.id}`)
        .then(res => setVehicles(res.data.vehicles || []))
        .catch(console.error);
    }
  }, [user]);

  const handleVehicleSelect = (vehicleId) => {
    setSelectedVehicles(prev => 
      prev.includes(vehicleId) ? prev.filter(id => id !== vehicleId) : [...prev, vehicleId]
    );
  };

  return (
    <div className="screen-container customer-theme">
      <header className="app-header">
        <div className="header-left" onClick={() => setShowProfileMenu(true)}>
          <div className="avatar">{user?.name?.[0] || 'U'}</div>
          <div className="greeting-section">
            <span className="greeting">Hello,</span>
            <span className="user-name">{user?.name || 'Customer'}</span>
          </div>
        </div>
        <div className="header-right">
          <button className="icon-button" onClick={() => navigate('/customer/messages')}>💬</button>
          <button className="icon-button">🔔</button>
        </div>
      </header>

      <nav className="nav-bar">
        {[
          { icon: '🏠', label: 'Home', active: true },
          { icon: '🔧', label: 'Services' },
          { icon: '🤖', label: 'CarmaAI', onClick: () => navigate('/customer/ai') },
          { icon: '📍', label: 'Track' },
          { icon: '📋', label: 'History' }
        ].map((item, i) => (
          <button key={i} className={`nav-item ${item.active ? 'active' : ''}`} onClick={item.onClick}>
            <span>{item.icon}</span>
            {item.active && <div className="active-indicator"></div>}
          </button>
        ))}
      </nav>

      <div className="content">
        <div className="quick-access-grid">
          <div className="quick-action-card" onClick={() => navigate('/customer/services')}>
            <span className="card-icon">➕</span>
            <span className="quick-action-title">Service Request</span>
          </div>
          <div className="quick-action-card accent" onClick={() => navigate('/customer/ai')}>
            <span className="card-icon">✨</span>
            <span className="quick-action-title">CarmaAI</span>
          </div>
        </div>

        <div className="garage-section">
          <div className="section-title">My Garage - Select to Start</div>
          <div className="swipe-instruction">Swipe to browse • Tap to select</div>
          
          <div className="vehicle-scroll">
            {vehicles.length > 0 ? vehicles.map(v => (
              <div 
                key={v._id || v.id} 
                className={`vehicle-card ${selectedVehicles.includes(v._id || v.id) ? 'selected' : ''}`}
                onClick={() => handleVehicleSelect(v._id || v.id)}
              >
                <div className="vehicle-icon-container">🚗</div>
                <div className="vehicle-name">{v.year} {v.make} {v.model}</div>
                <div className="select-vehicle-row">
                  <div className={`checkbox ${selectedVehicles.includes(v._id || v.id) ? 'selected' : ''}`}>
                    {selectedVehicles.includes(v._id || v.id) && '✓'}
                  </div>
                  <span className="select-vehicle-text">Select Vehicle</span>
                </div>
              </div>
            )) : (
              <div className="empty-state">
                <span style={{ fontSize: '64px' }}>🚗</span>
                <div className="empty-state-title">No vehicles yet</div>
                <button className="continue-btn" style={{ marginTop: '16px', width: 'auto', padding: '12px 24px' }}>
                  ➕ Add Vehicle
                </button>
              </div>
            )}
          </div>

          {selectedVehicles.length > 0 && (
            <div className="continue-section">
              <button className="continue-btn" onClick={() => navigate('/customer/services')}>
                Continue <span>→</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {showProfileMenu && (
        <div className="modal-overlay" onClick={() => setShowProfileMenu(false)}>
          <div className="profile-menu" onClick={e => e.stopPropagation()}>
            <div className="profile-menu-header">
              <div className="profile-avatar">{user?.name?.[0] || 'U'}</div>
              <div className="profile-name">{user?.name}</div>
              <div className="profile-email">{user?.email}</div>
            </div>
            <div className="profile-menu-items">
              {[
                { icon: '👤', label: 'My Profile' },
                { icon: '🚗', label: 'My Vehicles' },
                { icon: '📋', label: 'Booking History' },
                { icon: '💳', label: 'Payment Methods' },
                { icon: '⚙️', label: 'Settings' },
                { icon: '❓', label: 'Help & Support' }
              ].map((item, i) => (
                <div key={i} className="profile-menu-item">
                  <span>{item.icon}</span>
                  <span>{item.label}</span>
                  <span className="chevron">›</span>
                </div>
              ))}
              <div className="profile-menu-item logout-item" onClick={() => { logout(); navigate('/'); }}>
                <span>🚪</span>
                <span>Logout</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const CustomerServices = () => {
  const navigate = useNavigate();
  const [selectedServices, setSelectedServices] = useState([]);

  const services = [
    { id: '1', name: 'Oil Change', price: 49.99, desc: 'Full synthetic oil change' },
    { id: '2', name: 'Brake Inspection', price: 29.99, desc: 'Complete brake system check' },
    { id: '3', name: 'Tire Rotation', price: 24.99, desc: 'Rotate all 4 tires' },
    { id: '4', name: 'Battery Check', price: 19.99, desc: 'Test battery health' },
    { id: '5', name: 'A/C Service', price: 79.99, desc: 'Recharge A/C system' },
    { id: '6', name: 'Diagnostic Scan', price: 59.99, desc: 'Full OBD-II scan' }
  ];

  const toggleService = (service) => {
    setSelectedServices(prev => 
      prev.find(s => s.id === service.id)
        ? prev.filter(s => s.id !== service.id)
        : [...prev, service]
    );
  };

  const total = selectedServices.reduce((sum, s) => sum + s.price, 0);

  return (
    <div className="screen-container customer-theme">
      <header className="simple-header">
        <button onClick={() => navigate('/customer/home')} className="back-btn">←</button>
        <h1>Select Services</h1>
      </header>

      <div className="services-container">
        {services.map(service => (
          <div
            key={service.id}
            className={`service-item ${selectedServices.find(s => s.id === service.id) ? 'selected' : ''}`}
            onClick={() => toggleService(service)}
          >
            <div className={`service-checkbox ${selectedServices.find(s => s.id === service.id) ? 'selected' : ''}`}>
              {selectedServices.find(s => s.id === service.id) && '✓'}
            </div>
            <div className="service-details">
              <div className="service-name">{service.name}</div>
              <div className="service-desc">{service.desc}</div>
            </div>
            <div className="service-price">${service.price}</div>
          </div>
        ))}

        {selectedServices.length > 0 && (
          <div className="grand-total-section">
            <div className="total-row">
              <span className="total-label">Total</span>
              <span className="total-value">${total.toFixed(2)}</span>
            </div>
          </div>
        )}
      </div>

      {selectedServices.length > 0 && (
        <div className="bottom-bar">
          <button className="back-btn-outline" onClick={() => navigate('/customer/home')}>← Back</button>
          <button className="continue-btn" style={{ flex: 2 }}>Continue →</button>
        </div>
      )}
    </div>
  );
};

const CustomerAI = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [messages, setMessages] = useState([
    { role: 'ai', text: 'Hi! I\'m CarmaAI 🤖 How can I help you with your vehicle today?' }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const userMsg = { role: 'user', text: input };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const res = await axios.post(`${API}/ai/chat`, { message: input, user_id: user?._id || 'anonymous' });
      setMessages(prev => [...prev, { role: 'ai', text: res.data.response }]);
    } catch {
      setMessages(prev => [...prev, { role: 'ai', text: 'Sorry, I\'m having trouble connecting. Please try again.' }]);
    }
    setLoading(false);
  };

  return (
    <div className="chat-container customer-theme">
      <header className="chat-header">
        <button onClick={() => navigate('/customer/home')} className="back-btn">←</button>
        <div className="ai-icon-container">🤖</div>
        <div className="chat-header-info">
          <div className="chat-header-title">CarmaAI</div>
          <div className="chat-header-subtitle">Your vehicle assistant</div>
        </div>
      </header>

      <div className="messages-container">
        {messages.map((msg, i) => (
          <div key={i} className={`message-bubble ${msg.role}`}>
            {msg.role === 'ai' && <div className="ai-avatar">🤖</div>}
            <div className={`message-content ${msg.role}`}>
              <p className="message-text">{msg.text}</p>
            </div>
          </div>
        ))}
        {loading && (
          <div className="message-bubble ai">
            <div className="ai-avatar">🤖</div>
            <div className="message-content ai">Thinking...</div>
          </div>
        )}
      </div>

      <div className="chat-input-container">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
          placeholder="Ask about your car..."
          className="chat-input"
        />
        <button onClick={sendMessage} disabled={loading || !input.trim()} className="send-btn">➤</button>
      </div>
    </div>
  );
};

const CustomerPortal = () => {
  const [view, setView] = useState('login');
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (isAuthenticated && location.pathname === '/customer') {
      navigate('/customer/home');
    }
  }, [isAuthenticated, location.pathname, navigate]);

  if (!isAuthenticated) {
    return view === 'login' 
      ? <CustomerLoginScreen onNavigate={setView} />
      : <CustomerRegisterScreen onNavigate={setView} />;
  }

  return (
    <Routes>
      <Route path="home" element={<CustomerHome />} />
      <Route path="services" element={<CustomerServices />} />
      <Route path="ai" element={<CustomerAI />} />
      <Route path="*" element={<CustomerHome />} />
    </Routes>
  );
};

// ==================== MECHANIC PORTAL ====================
const MechanicLoginScreen = ({ onNavigate }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) { setError('Please fill in all fields'); return; }
    setLoading(true);
    const result = await login(email, password, 'mechanic');
    if (result.success) { navigate('/mechanic/home'); }
    else { setError(result.error); }
    setLoading(false);
  };

  return (
    <div className="login-container mechanic-theme">
      <div className="logo-section">
        <img src="/logo.png" alt="CarmaGard Logo" onError={(e) => e.target.src = ''} />
      </div>
      <p className="mechanic-portal-label">Mechanic Portal</p>
      {error && <div className="error-alert">{error}</div>}
      <form onSubmit={handleLogin}>
        <div className="form-group">
          <label className="form-label">Email</label>
          <input 
            type="email" 
            className="form-input" 
            value={email} 
            onChange={(e) => setEmail(e.target.value)} 
            placeholder="your.email@example.com"
          />
        </div>
        <div className="form-group">
          <label className="form-label">Password</label>
          <div className="password-container">
            <input 
              type={showPassword ? 'text' : 'password'} 
              className="form-input" 
              value={password} 
              onChange={(e) => setPassword(e.target.value)} 
              placeholder="Enter your password"
            />
            <button type="button" className="password-toggle" onClick={() => setShowPassword(!showPassword)}>
              {showPassword ? '🙈' : '👁️'}
            </button>
          </div>
        </div>
        <div className="forgot-password">
          <a href="#">Forgot Password?</a>
        </div>
        <button type="submit" className="login-btn" disabled={loading}>
          {loading ? <span className="spinner"></span> : 'Login'}
        </button>
      </form>
      <div className="signup-section">
        <span>New mechanic? </span>
        <a href="#" onClick={(e) => { e.preventDefault(); onNavigate('register'); }}>Sign Up</a>
      </div>
      <button onClick={() => navigate('/')} className="back-home-btn">← Back to Home</button>
    </div>
  );
};

const MechanicRegisterScreen = ({ onNavigate }) => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register, login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const result = await register(formData, 'mechanic');
    if (result.success) {
      await login(formData.email, formData.password, 'mechanic');
      navigate('/mechanic/home');
    } else { setError(result.error); }
    setLoading(false);
  };

  return (
    <div className="login-container mechanic-theme">
      <h1 className="register-title">Mechanic Registration</h1>
      {error && <div className="error-alert">{error}</div>}
      <form onSubmit={handleSubmit}>
        {['name', 'email', 'phone', 'password'].map((field) => (
          <div key={field} className="form-group">
            <label className="form-label">{field.charAt(0).toUpperCase() + field.slice(1)}</label>
            <input
              type={field === 'password' ? 'password' : field === 'email' ? 'email' : 'text'}
              className="form-input"
              value={formData[field]}
              onChange={(e) => setFormData({...formData, [field]: e.target.value})}
              required
            />
          </div>
        ))}
        <button type="submit" className="login-btn" disabled={loading}>
          {loading ? <span className="spinner"></span> : 'Register'}
        </button>
      </form>
      <div className="signup-section">
        <span>Already registered? </span>
        <a href="#" onClick={(e) => { e.preventDefault(); onNavigate('login'); }}>Sign In</a>
      </div>
    </div>
  );
};

const MechanicHome = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isOnline, setIsOnline] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [activeTab, setActiveTab] = useState('home');

  useEffect(() => {
    axios.get(`${API}/jobs/available`).then(res => setJobs(res.data.available_jobs || [])).catch(console.error);
  }, []);

  return (
    <div className="screen-container mechanic-theme">
      <header className="mechanic-header">
        <div className="mechanic-logo-section">
          <img src="/logo.png" alt="CarmaGard" className="mechanic-header-logo" onError={(e) => e.target.style.display = 'none'} />
          <span className="mechanic-header-text">Mechanic</span>
        </div>
        <div className="status-toggle" onClick={() => setIsOnline(!isOnline)}>
          <div className={`status-dot ${isOnline ? '' : 'offline'}`}></div>
          <span>{isOnline ? 'Online' : 'Offline'}</span>
        </div>
      </header>

      <div className="main">
        <div className="stats-grid">
          <div className="stat">
            <div className="stat-value">$0</div>
            <div className="stat-label">Today</div>
          </div>
          <div className="stat">
            <div className="stat-value">{jobs.length}</div>
            <div className="stat-label">Available</div>
          </div>
          <div className="stat">
            <div className="stat-value">5.0</div>
            <div className="stat-label">Rating</div>
          </div>
        </div>

        <div className="card">
          <h2>✨ Available Jobs</h2>
          {jobs.length === 0 ? (
            <div className="empty">No jobs available right now</div>
          ) : (
            jobs.map(job => (
              <div key={job._id} className="job-card">
                <div className="job-header">
                  <div className="job-title">{job.service_type || 'Service Request'}</div>
                  <div className="job-price">${job.total_cost || 100}</div>
                </div>
                <div className="job-details">
                  <p>📍 {job.location?.address || 'Location TBD'}</p>
                </div>
                <div className="job-actions">
                  <button className="btn btn-accept">Accept</button>
                  <button className="btn btn-decline">Decline</button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <nav className="nav-bottom">
        {[
          { icon: '🏠', label: 'Home', id: 'home' },
          { icon: '📋', label: 'Jobs', id: 'jobs' },
          { icon: '💰', label: 'Earnings', id: 'earnings' },
          { icon: '👤', label: 'Profile', id: 'profile', onClick: () => { logout(); navigate('/'); } }
        ].map(item => (
          <div key={item.id} className={`nav-item ${activeTab === item.id ? 'active' : ''}`} onClick={item.onClick || (() => setActiveTab(item.id))}>
            <span>{item.icon}</span>
            {item.label}
          </div>
        ))}
      </nav>
    </div>
  );
};

const MechanicPortal = () => {
  const [view, setView] = useState('login');
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (isAuthenticated && location.pathname === '/mechanic') {
      navigate('/mechanic/home');
    }
  }, [isAuthenticated, location.pathname, navigate]);

  if (!isAuthenticated) {
    return view === 'login' 
      ? <MechanicLoginScreen onNavigate={setView} />
      : <MechanicRegisterScreen onNavigate={setView} />;
  }

  return (
    <Routes>
      <Route path="home" element={<MechanicHome />} />
      <Route path="*" element={<MechanicHome />} />
    </Routes>
  );
};

// ==================== ADMIN PORTAL ====================
const AdminLoginScreen = () => {
  const [email, setEmail] = useState('admin@carmagard.com');
  const [password, setPassword] = useState('admin123');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    const result = await login(email, password, 'admin');
    if (result.success) { navigate('/admin/dashboard'); }
    else { setError(result.error); }
    setLoading(false);
  };

  return (
    <div className="login-container admin-theme">
      <div className="login-header">
        <h1>⚙️ Admin Portal</h1>
        <p>CarmaGard Management</p>
      </div>
      {error && <div className="error-alert">{error}</div>}
      <form onSubmit={handleLogin}>
        <div className="form-group">
          <label>Email</label>
          <input type="email" className="form-input" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="form-group">
          <label>Password</label>
          <input type="password" className="form-input" value={password} onChange={(e) => setPassword(e.target.value)} />
        </div>
        <button type="submit" className="login-btn admin" disabled={loading}>
          {loading ? <span className="spinner"></span> : 'Login'}
        </button>
      </form>
      <button onClick={() => navigate('/')} className="back-home-btn">← Back to Home</button>
    </div>
  );
};

const AdminDashboard = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ total_customers: 0, total_mechanics: 0, total_bookings: 0, total_revenue: 0 });
  const [activeTab, setActiveTab] = useState('dashboard');
  const [data, setData] = useState([]);

  useEffect(() => {
    axios.get(`${API}/admin/dashboard`).then(res => setStats(res.data)).catch(console.error);
  }, []);

  useEffect(() => {
    const endpoints = { customers: '/admin/customers', mechanics: '/admin/mechanics', bookings: '/admin/bookings' };
    if (endpoints[activeTab]) {
      axios.get(`${API}${endpoints[activeTab]}`).then(res => setData(res.data || [])).catch(console.error);
    }
  }, [activeTab]);

  return (
    <div className="admin-layout">
      <aside className="admin-sidebar">
        <div className="admin-logo">⚙️ CarmaGard Admin</div>
        <nav>
          {[
            { id: 'dashboard', icon: '📊', label: 'Dashboard' },
            { id: 'customers', icon: '👥', label: 'Customers' },
            { id: 'mechanics', icon: '🔧', label: 'Mechanics' },
            { id: 'bookings', icon: '📋', label: 'Bookings' }
          ].map(item => (
            <button key={item.id} onClick={() => setActiveTab(item.id)} className={`sidebar-item ${activeTab === item.id ? 'active' : ''}`}>
              <span>{item.icon}</span> {item.label}
            </button>
          ))}
          <button onClick={() => { logout(); navigate('/'); }} className="sidebar-item logout">
            <span>🚪</span> Logout
          </button>
        </nav>
      </aside>

      <main className="admin-main">
        <h1>{activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}</h1>

        {activeTab === 'dashboard' && (
          <div className="stats-cards">
            {[
              { label: 'Total Customers', value: stats.total_customers, color: 'blue' },
              { label: 'Total Mechanics', value: stats.total_mechanics, color: 'green' },
              { label: 'Total Bookings', value: stats.total_bookings, color: 'purple' },
              { label: 'Revenue', value: `$${(stats.total_revenue || 0).toFixed(2)}`, color: 'orange' }
            ].map((stat, i) => (
              <div key={i} className={`stat-card ${stat.color}`}>
                <p className="stat-label">{stat.label}</p>
                <p className="stat-value">{stat.value}</p>
              </div>
            ))}
          </div>
        )}

        {activeTab !== 'dashboard' && (
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  {activeTab === 'customers' && <><th>Name</th><th>Email</th><th>Phone</th><th>Status</th></>}
                  {activeTab === 'mechanics' && <><th>Name</th><th>Email</th><th>Rating</th><th>Status</th></>}
                  {activeTab === 'bookings' && <><th>ID</th><th>Service</th><th>Date</th><th>Status</th></>}
                </tr>
              </thead>
              <tbody>
                {data.slice(0, 20).map((item, i) => (
                  <tr key={item._id || i}>
                    {activeTab === 'customers' && <><td>{item.name || 'N/A'}</td><td>{item.email}</td><td>{item.phone || 'N/A'}</td><td><span className="badge active">Active</span></td></>}
                    {activeTab === 'mechanics' && <><td>{item.name || 'N/A'}</td><td>{item.email}</td><td>⭐ {item.rating || 5.0}</td><td><span className={`badge ${item.available ? 'active' : 'inactive'}`}>{item.available ? 'Online' : 'Offline'}</span></td></>}
                    {activeTab === 'bookings' && <><td>{item._id?.slice(-6)}</td><td>{item.service_type || 'Service'}</td><td>{item.scheduled_time ? new Date(item.scheduled_time).toLocaleDateString() : 'TBD'}</td><td><span className={`badge ${item.status}`}>{item.status}</span></td></>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
};

const AdminPortal = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (isAuthenticated && location.pathname === '/admin') {
      navigate('/admin/dashboard');
    }
  }, [isAuthenticated, location.pathname, navigate]);

  if (!isAuthenticated) return <AdminLoginScreen />;

  return (
    <Routes>
      <Route path="dashboard" element={<AdminDashboard />} />
      <Route path="*" element={<AdminDashboard />} />
    </Routes>
  );
};

// ==================== PWA REDIRECT COMPONENTS ====================
// These components handle the case when React Router intercepts /pwa/* routes
// They force a hard reload to serve the static PWA files from the public folder

const PWARedirect = ({ target }) => {
  useEffect(() => {
    // Force a hard navigation to bypass React Router and serve static files
    window.location.replace(target);
  }, [target]);
  
  return (
    <div style={{ 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      height: '100vh', 
      background: '#000',
      color: '#FF8C42',
      flexDirection: 'column',
      gap: '16px'
    }}>
      <div style={{ fontSize: '48px' }}>🔧</div>
      <p>Loading CarmaGard...</p>
    </div>
  );
};

const CustomerPWARedirect = () => <PWARedirect target="/pwa/customer/index.html" />;
const MechanicPWARedirect = () => <PWARedirect target="/pwa/mechanic/index.html" />;
const AdminPWARedirect = () => <PWARedirect target="/pwa/admin/index.html" />;

// ==================== MAIN APP ====================
function App() {
  return (
    <AuthProvider>
      <BookingProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            {/* PWA Routes - redirect to static HTML files */}
            <Route path="/pwa/customer/*" element={<CustomerPWARedirect />} />
            <Route path="/pwa/mechanic/*" element={<MechanicPWARedirect />} />
            <Route path="/pwa/admin/*" element={<AdminPWARedirect />} />
            {/* Legacy routes - also redirect to PWAs */}
            <Route path="/customer/*" element={<CustomerPWARedirect />} />
            <Route path="/mechanic/*" element={<MechanicPWARedirect />} />
            <Route path="/admin/*" element={<AdminPWARedirect />} />
          </Routes>
        </BrowserRouter>
      </BookingProvider>
    </AuthProvider>
  );
}

export default App;
